import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Activity, LayoutList, GitBranch, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { CardSkeleton } from "@/components/loading";
import { useActivityLogsQuery } from "@/hooks/use-activity-logs";
import type { ActivityLogListItem } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { LogCard } from "@/components/admin/audit-logs/LogCard";
import { LogFilters, type LogFiltersValue } from "@/components/admin/audit-logs/LogFilters";
import { LogSearch } from "@/components/admin/audit-logs/LogSearch";
import { LogTimeline } from "@/components/admin/audit-logs/LogTimeline";
import { LogDetailDrawer } from "@/components/admin/audit-logs/LogDetailDrawer";

export const Route = createFileRoute("/_authenticated/admin/audit-logs")({
  component: AdminAuditLogsPage,
});

const PAGE_SIZE = 20;

type ViewMode = "list" | "timeline";

function AdminAuditLogsPage() {
  const { isAdmin } = useAuth();

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<LogFiltersValue>({});
  const [page, setPage] = useState(1);
  const [view, setView] = useState<ViewMode>("list");
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const hasActiveFilters =
    !!search || Object.values(filters).some((v) => v !== undefined && v !== "");

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function updateFilters(patch: Partial<LogFiltersValue>) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }

  function clearAll() {
    setSearch("");
    setFilters({});
    setPage(1);
  }

  const query = useMemo(
    () => ({
      ...filters,
      search: search.trim() || undefined,
      page,
      pageSize: PAGE_SIZE,
      sortBy: "timestamp",
      sortDesc: true,
    }),
    [filters, search, page],
  );

  const { data, isLoading, isFetching, refetch } = useActivityLogsQuery(query, isAdmin);

  const logs: ActivityLogListItem[] = data?.items ?? [];

  if (!isAdmin) {
    return <AccessDenied message="You don't have permission to view audit logs." />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-4 pb-2 border-b border-muted">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary shadow-inner">
              <Activity className="h-7 w-7" />
            </div>
            Audit Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Full audit trail of platform activity — logins, content changes, certificates, and
            system events.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2 hover:bg-muted/50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            {isFetching ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Search + view toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <LogSearch value={search} onChange={handleSearchChange} />
        <div className="flex items-center rounded-xl border bg-card p-1 shrink-0">
          <button
            type="button"
            onClick={() => setView("list")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              view === "list"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <LayoutList className="h-3.5 w-3.5" />
            List
          </button>
          <button
            type="button"
            onClick={() => setView("timeline")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              view === "timeline"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <GitBranch className="h-3.5 w-3.5" />
            Timeline
          </button>
        </div>
      </div>

      {/* Filters */}
      <LogFilters
        value={filters}
        onChange={updateFilters}
        onClear={clearAll}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Result summary */}
      <p className="text-xs text-muted-foreground">
        {isLoading
          ? "Loading activity…"
          : `Showing ${logs.length} of ${data?.totalCount ?? 0} log entries`}
      </p>

      {/* Content */}
      {isLoading ? (
        <CardSkeleton count={6} className="grid-cols-1 sm:grid-cols-1 lg:grid-cols-1" />
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border rounded-2xl bg-card">
          <Activity className="h-10 w-10 mx-auto mb-3 opacity-30 text-primary" />
          <p className="text-sm font-medium">No activity log entries match the current filters.</p>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={clearAll}>
              Clear filters
            </Button>
          )}
        </div>
      ) : view === "list" ? (
        <div className="space-y-3">
          {logs.map((log) => (
            <LogCard key={log.id} log={log} onSelect={(l) => setSelectedLogId(l.id)} />
          ))}
        </div>
      ) : (
        <LogTimeline logs={logs} onSelect={(l) => setSelectedLogId(l.id)} />
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (data.hasPreviousPage) setPage((p) => Math.max(1, p - 1));
                }}
                className={!data.hasPreviousPage ? "pointer-events-none opacity-40" : undefined}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#" isActive className="pointer-events-none">
                {data.pageNumber} / {data.totalPages}
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (data.hasNextPage) setPage((p) => p + 1);
                }}
                className={!data.hasNextPage ? "pointer-events-none opacity-40" : undefined}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <LogDetailDrawer logId={selectedLogId} onClose={() => setSelectedLogId(null)} />
    </div>
  );
}
